import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  input,
  signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UsersManagementStore } from '../../store/users-management.store';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { PhoneValidators } from '../../../../../shared/validators';
import { AvatarComponent } from '../../../../../shared/ui/avatar/avatar.component';
import { AuthStore } from '../../../login/store/auth.store';
import { RoleInfoComponent } from '../../../../../shared/ui/role-info/role-info.component';
import { IRole } from '../../../roles-management/interfaces';
import { CloseButtonComponent } from '../../../../../shared/ui/close-button/close-button.component';
import { RolesManagementStore } from '../../../roles-management/store/roles-management.store';
import {
  SharedUIService,
  DialogHelpersService,
} from '../../../../../shared/services';
import { TranslatePipe } from '../../../../shared/pipes/translationPipe';
import { AsyncPipe } from '@angular/common';
import { IProfileEdit, IProfileEditForm } from '../../interfaces';
import { AppStore } from '../../../../app.store';

@Component({
  selector: 'app-profile-edit',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatMenuModule,
    MatAutocompleteModule,
    AvatarComponent,
    RoleInfoComponent,
    CloseButtonComponent,
    TranslatePipe,
    AsyncPipe,
  ],
  templateUrl: './profile-edit.component.html',
  styleUrl: './profile-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileEditComponent implements OnInit {
  readonly store = inject(UsersManagementStore);
  readonly authStore = inject(AuthStore);
  readonly appStore = inject(AppStore);
  readonly rolesStore = inject(RolesManagementStore);
  readonly phoneValidator = inject(PhoneValidators);
  readonly dialogRef = inject(MatDialogRef<ProfileEditComponent>, {
    optional: true,
  });
  readonly #fb = inject(NonNullableFormBuilder);
  readonly #phoneValidator = inject(PhoneValidators);
  protected sharedUI = inject(SharedUIService);
  readonly #dialogHelpers = inject(DialogHelpersService);

  selfUserEdit = input<boolean>(false);
  showClose = input<boolean>(true);
  roleChanged = signal<boolean>(false);

  form = this.#fb.group<IProfileEditForm>({
    firstName: this.#fb.control(this.store.tempUser()?.firstName || '', [
      Validators.required,
    ]),
    lastName: this.#fb.control(this.store.tempUser()?.lastName || '', [
      Validators.required,
    ]),
    userRole: this.#fb.control(this.store.tempUser()?.userRole as IRole, [
      Validators.required,
    ]),
    phoneNumber: this.#fb.control(this.store.tempUser()?.phoneNumber || '', [
      this.#phoneValidator.uniquePhoneValidator(
        this.store.entities(),
        this.store.tempUser()?.id ?? '',
      ),
    ]),
  });

  protected showConfirmationDialog = () => {
    this.sharedUI.showConfirmationWithObservable(
      this.#dialogHelpers.getDraftDialogData(),
      this.closeDialog,
    );
  };

  ngOnInit(): void {
    if (this.dialogRef) {
      this.sharedUI.confirmOnCloseWithObservable(
        this.dialogRef,
        () => (this.form.dirty && this.form.valid) || this.roleChanged(),
        this.#dialogHelpers.getDraftDialogData(),
      );
    }
  }

  onSubmit() {
    if (!this.form.valid) return;

    const rawUser = this.form.getRawValue();
    const fullName = `${rawUser.firstName} ${rawUser.lastName}`;

    if (!this.selfUserEdit()) {
      const updatedUser = {
        ...rawUser,
        id: this.store.tempUser()?.id,
      };
      this.store.checkUniqueName(fullName, updatedUser, this.dialogRef!);
      if (updatedUser.id === this.authStore.currentUser()?.id) {
        this.authStore.updateCurrentUser(updatedUser);
      }
    } else {
      const currentUserId = this.authStore.currentUser()?.id;
      this.store.checkUniqueName(fullName, {
        ...rawUser,
        id: currentUserId,
      } as IProfileEdit & { id: string });
      this.authStore.updateCurrentUser({
        ...rawUser,
        id: currentUserId,
      });
      this.appStore.toggleUserProfile();
    }
  }

  closeDialog = () => {
    if (this.dialogRef) {
      this.sharedUI.closeDialog(this.dialogRef);
    }
  };
}
